import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    useArealplaner,
    usePlanforhold,
    useCreatePlanforhold,
    useDeletePlanforhold,
} from '@origo/planregister';
import { Loader, Notice } from '@origo/origo-ui';
import RelationsPage from './RelationsPage';
import type { RelationEditDialogOptions } from './components/RelationEditDialog';
import { forholdtypeOptions } from '../../codelists/forholdtype';
import { buildPartyLookup, toRelationPair } from '../../mappers/toRelations';
import type { RelationFormValues, SelectOption } from '../../types';

interface RelationsContainerProps {
    napId: string;
}

const initialDialog: RelationFormValues = {
    motpartPlanId: '',
    type: '',
    ikrafttredelsesdato: '',
    status: '',
};

const RelationsContainer: React.FC<RelationsContainerProps> = ({ napId }) => {
    const { t } = useTranslation();
    const planforhold = usePlanforhold(napId);
    const allPlans = useArealplaner();
    const createRelation = useCreatePlanforhold(napId);
    const deleteRelation = useDeletePlanforhold(napId);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState<RelationFormValues>(initialDialog);

    const lookup = useMemo(() => buildPartyLookup(allPlans.data ?? []), [allPlans.data]);

    const pairs = useMemo(
        () => (planforhold.data ?? []).map(rel => toRelationPair(rel, lookup, t)),
        [planforhold.data, lookup, t]
    );

    const motpartOptions: SelectOption[] = useMemo(
        () => (allPlans.data ?? [])
            .filter(p => p.nasjonalArealplanId !== napId)
            .map(p => ({ value: p.nasjonalArealplanId, label: `${p.plannavn} — ${p.planId}` })),
        [allPlans.data, napId]
    );

    const dialogOptions: RelationEditDialogOptions = useMemo(
        () => ({
            motpart: motpartOptions,
            type: forholdtypeOptions(t),
            ikrafttredelsesdato: [],
            status: [],
        }),
        [motpartOptions, t]
    );

    const handleSave = () => {
        const editing = editingId
            ? planforhold.data?.find(p => p.relasjonId === editingId)
            : undefined;
        const submit = () => {
            createRelation.mutate({
                relasjonId: editing?.relasjonId ?? crypto.randomUUID(),
                nasjonalArealplanIdFra: napId,
                nasjonalArealplanIdTil: form.motpartPlanId,
                forholdtype: form.type as never,
                vedtakEndeligPlanDato: form.ikrafttredelsesdato ? new Date(form.ikrafttredelsesdato) : undefined,
            });
            setDialogOpen(false);
            setEditingId(null);
            setForm(initialDialog);
        };
        if (editing) {
            // Domain mangler update — gjør delete + create.
            deleteRelation.mutate(editing.objectId, { onSuccess: submit });
        } else {
            submit();
        }
    };

    if (planforhold.isLoading || allPlans.isLoading) {
        return <Loader label={t('common.loading')} />;
    }
    if (planforhold.isError || allPlans.isError) {
        return <Notice variant="danger">{t('common.errorLoading')}</Notice>;
    }

    return (
        <RelationsPage
            pairs={pairs}
            dialogOpen={dialogOpen}
            dialogValues={form}
            dialogOptions={dialogOptions}
            onEdit={id => {
                const rel = planforhold.data?.find(p => p.relasjonId === id);
                setEditingId(id);
                setForm({
                    motpartPlanId: rel?.nasjonalArealplanIdTil ?? '',
                    type: rel?.forholdtype ?? '',
                    ikrafttredelsesdato: rel?.vedtakEndeligPlanDato?.toISOString().slice(0, 10) ?? '',
                    status: '',
                });
                setDialogOpen(true);
            }}
            onDialogChange={(field, value) => setForm(prev => ({ ...prev, [field]: value }))}
            onDialogClose={() => {
                setDialogOpen(false);
                setEditingId(null);
                setForm(initialDialog);
            }}
            onDialogSave={handleSave}
        />
    );
};

export default RelationsContainer;
